<template>
  <div v-if="!authStore.isAuthenticated || (authStore.isAuthenticated && authStore.account && shouldHaveInstanceID) || authStore.maintenanceModeIsOn" class="w-full flex flex-col sm:!flex-row overscroll-none">
    <vs-omni-search
      v-model="isSearching"
      :results="searchResults"
      @search:changed="runSearch"
      @search:select="$router.push($event.path)"
    />
    <vs-instance-picker
      v-model="showInstancePicker"
      :instances="authStore.instances"
      :currentInstanceID="authStore.instanceID"
      @instance:selected="authStore.setInstanceID($event)"
    />
    <vs-sidebar
      v-if="!authStore.toolbarHidden && authStore.isAdmin && !authStore.maintenanceModeIsOn"
      :pages="adminPages"
      :active-page="activePage"
      @page:select="handlePageSelect"
    >
      <template #header="{ isCollapsed, isSmall }">
        <div class="flex items-center w-full justify-center h-20 mb-2">
          <a href="/">
            <img src="@/assets/logo.svg" :width="isCollapsed && !isSmall ? 40 : 50" />
          </a>
        </div>
        <div v-if="authStore.instanceID">
          <div class="mb-5 flex justify-center w-full">
            <vs-avatar
              class="cursor-pointer hover:brightness-90"
              :name="currentInstance?.name"
              @click="showInstancePicker = true"
            />
          </div>
          <div
            class="mx-4 mb-2 bg-surface px-2 py-2 flex items-center rounded cursor-pointer transition"
            :class="authStore.gettingSearchKey ? 'opacity-50 cursor-wait' : ''"
            @click="!authStore.gettingSearchKey && (isSearching = true)"
          >
            <vs-icon class="opacity-50 mr-2" name="akar-icons:search" />
            <div v-if="!isCollapsed || isSmall">
              Buscar
            </div>
          </div>
        </div>
        <div v-else>
          <div class="flex justify-center p-4">
            <vs-preloader color="surface" :size=36></vs-preloader>
          </div>
        </div>
      </template>
    </vs-sidebar>
    <div 
      class="overflow-y-auto sm:w-9/12 sm:min-w-150 w-full flex flex-col flex-grow"
      :class="authStore.isAdmin ? 'h-[calc(100vh-46px-env(safe-area-inset-bottom))] sm:h-screen' : 'h-screen'"  
    >
      <Suspense>
        <div
          class="relative flex-grow flex-shrink overflow-auto overscroll-none"
          :class="authStore.roles.includes('user') && !authStore.isAdmin ? 'sm:pb-0' : ''"
        >
          <!-- No route has matched until the first navigation's guards (claims, account, maintenance) and lazy chunk
               resolve: keep the boot screen up instead of an empty page. -->
          <app-boot-screen v-if="!route.matched.length" :caption="bootCaption" />
          <router-view
            :key="getRouteKey($route.path)"
            v-slot="{ Component }"
          >
            <transition 
              :name="route.meta.transitionName"
              mode="out-in"
            >
              <component :is="Component" />
            </transition>
          </router-view>
        </div>
      </Suspense>
      <div v-if="uiStore.map.teleportTo && !os">
        <Teleport
          :to="uiStore.map.teleportTo"
        >
          <vs-map
            :ref="setMapRef"
            v-bind="uiStore.map.attrs"
            v-if="!os"
            :locationPermission="false"
            @location:confirm="uiStore.map.attrs['v-on:location:confirm']"
            @ready="uiStore.setReady($event)"
            @click="handleMapClick"
            @marker:drag="handleMarkerDrag"
            @marker:infoClick="handleInfoClick"
            @marker:click="handleMarkerClick"
          />
        </Teleport>
      </div>
      <vs-toolbar
        v-if="!authStore.toolbarHidden &&  authStore.roles.includes('user') && !authStore.isAdmin && !authStore.maintenanceModeIsOn"
        :pages="userPages"
        @page:select="handlePageSelect"
        :active-page="activePage"
        :compact="true"
      ></vs-toolbar>
    </div>
  </div>
  <app-boot-screen v-else :caption="bootCaption" />
  <modals-container></modals-container>
  <!-- Post-trip bus walkthrough (utils/busWalkthrough.ts): one at a time, opened by the stream end, the push, the top
       banner and by itself on app start/resume (promptOpenCheck). Can't be closed while it can still be done. -->
  <BusWalkthroughModal
    v-if="activeBusWalkthrough"
    :key="activeBusWalkthrough.tripID"
    :request="activeBusWalkthrough"
    @close="closeBusWalkthrough()"
  />
  <!-- Top banners: open bus walkthroughs with their countdown (every screen), then the update notice. -->
  <div
    v-if="showUpdateBanner || shownChecks.length"
    class="fixed inset-x-0 top-0 z-50 flex flex-col items-center gap-2 px-4 pointer-events-none"
    style="padding-top: calc(env(safe-area-inset-top) + 0.5rem)"
  >
    <BusWalkthroughBanner />
    <div
      v-if="showUpdateBanner"
      role="status"
      class="pointer-events-auto flex items-center gap-3 max-w-full rounded-xl border border-surface-variant bg-surface text-on-surface shadow-lg py-1.5 pl-3 pr-1.5 text-sm"
    >
      <vs-icon name="lucide:refresh-cw" color="on-surface" :size="16" />
      <span>Hay una nueva versión disponible.</span>
      <vs-button class="!py-1 !px-3" text="Recargar" @click="reloadForUpdate" />
      <button
        type="button"
        aria-label="Cerrar"
        class="p-1 rounded-lg opacity-60 hover:opacity-100"
        @click="dismissUpdateBanner"
      >
        <vs-icon name="lucide:x" color="on-surface" :size="16" />
      </button>
    </div>
  </div>
  <!-- Old Android WebView (utils/webviewUpdate.ts): blocks the app until it's updated. space-y, not gap: flex gap is
       one of the things that WebView lacks. -->
  <vs-modal :model-value="!!webviewUpdate" buttonless :closable="false">
    <div v-if="webviewUpdate" class="flex flex-col items-center space-y-4 p-1 text-center">
      <vs-icon name="lucide:smartphone" color="primary" :size="32" />
      <h3 class="text-lg font-semibold text-on-surface">Actualiza {{ webviewUpdate.name }}</h3>
      <p class="text-base text-on-surface-variant">
        Tu teléfono tiene una versión vieja de {{ webviewUpdate.name }}. Actualízala para seguir usando la app.
      </p>
      <a class="external w-full" :href="webviewUpdate.storeURL">
        <vs-button kind="primary" class="h-12 w-full" text="Actualizar" />
      </a>
    </div>
  </vs-modal>
  <!-- "Hay alta demanda" while the backend is stalled (services/backendStall.ts): calm, never takes a touch. -->
  <div
    v-if="backendStalled"
    class="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pointer-events-none"
    style="padding-bottom: calc(env(safe-area-inset-bottom) + 0.75rem)"
  >
    <div
      role="status"
      class="flex items-center gap-2 max-w-full rounded-xl border border-surface-variant bg-surface text-on-surface shadow-lg py-2 px-3 text-sm"
    >
      <vs-icon name="lucide:hourglass" color="on-surface" :size="16" />
      <span>{{ stallNotice }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onBeforeMount, onMounted, computed, inject, Ref} from 'vue'
import { storeToRefs } from 'pinia'
import { firebaseTools } from '@/services/firebase'
import { isSupported } from '@firebase/messaging'
import { useAuthStore, useUIStore } from '@/stores'
import { useRouter, useRoute } from 'vue-router'
import { urqlMutation } from '@/utils/urqlMutation'
import { registerToken } from '@/utils/pushToken'
import { client, renovateClient, hasSearchKey } from '@/services/typesense'
import { search } from "ss-search"
import { gql, useClientHandle } from '@urql/vue'
import { state } from '@/services/localStorage'
import { useEventBus } from '@vueuse/core'
import { useForceReload } from '@/composables/useForceReload'
import { webviewUpdateFor } from '@/utils/webviewUpdate'
import { useBackendStalled } from '@/composables/useBackendStalled'
import { STALL_NOTICE } from '@/services/backendStall'
import { vsOmniSearch, vsInstancePicker, vsSidebar, vsAvatar, vsIcon, vsMap, vsToolbar, vsPreloader, vsButton, vsModal } from '@isagaco/vuesaga'
import BusWalkthroughModal from '@/components/BusWalkthroughModal.vue'
import { activeBusWalkthrough, closeBusWalkthrough, flushBusWalkthroughQueue, promptOpenCheck, refreshPendingChecks, busWalkthroughVersion, shownChecks } from '@/utils/busWalkthrough'
import BusWalkthroughBanner from '@/components/BusWalkthroughBanner.vue'
import AppBootScreen from '@/components/appBootScreen.vue'
type roles = 'no-roles'|'user'|'staff'|'instanceAdmin'|'support'

window.googleApiLoaded = (e)=>{
  window.googleApi = true
}

export default defineComponent({
  name: 'App',
  components: {
    vsOmniSearch, vsInstancePicker, vsSidebar, vsAvatar, vsIcon, vsMap, vsToolbar, vsPreloader, vsButton, vsModal, BusWalkthroughModal, BusWalkthroughBanner, AppBootScreen
  },
  setup (props, {expose}) {
    const authStore = useAuthStore()
    // Bus walkthroughs saved offline go out on the next open, on reconnect and on app resume (staff sessions only);
    // then the checks still open reload into the top banner, also whenever one closes or syncs.
    const flushBusWalkthroughs = async () => {
      if (!authStore.account || !authStore.isStaff) return
      await flushBusWalkthroughQueue()
      await refreshPendingChecks()
      // Mandatory: an open check opens by itself on start, resume and reconnect (offline from the phone's copy).
      promptOpenCheck()
    }
    watch(busWalkthroughVersion, async () => {
      if (!authStore.account || !authStore.isStaff) return
      await refreshPendingChecks()
      promptOpenCheck()
    })
    watch(() => authStore.account?.id, flushBusWalkthroughs, { immediate: true })
    window.addEventListener('online', flushBusWalkthroughs)
    document.addEventListener('resume', flushBusWalkthroughs, false)
    const uiStore = useUIStore()
    const clientHandle = useClientHandle()
    // Opt-in reload of stale builds via RTDB appConfig/minBuildTime, plus the update banner.
    const { showUpdateBanner, dismissUpdateBanner, reloadForUpdate } = useForceReload()
    // Blocks every screen until the WebView is updated.
    const webviewUpdate = webviewUpdateFor(navigator.userAgent)
    const backendStalled = useBackendStalled()
    const stallNotice = STALL_NOTICE
    const $crisp = window.$crisp
    $crisp.push(["safe", true])
    const os = computed(() => {
      return window.os
    })
    const instances = computed(() => {
      return authStore.userInstances?.map(instance => instance.name)
    })
    const currentInstance = computed(() => {
      return authStore.instances.find(instance => {
        return instance.id === authStore.instanceID
      })
    })
    const parcels = computed(() => {
      return authStore.parcels?.map(parcel => {
        let str = `${parcel.name}: \n•${parcel.instance.name}\n`
        if (parcel.routes) {
          str += parcel.routes?.map(route=> '○' + route.name).join('\n')
        }
        return str
      }).join('\n\n')
    })
    watch(instances, () => {
      if (instances.value.length > 0) {
        $crisp.push(['set', 'session:segments', [instances.value]])
      }
    })
    watch(parcels, () => {
      $crisp.push(['set', 'session:data', [[['Pasajeros', parcels.value]]]])
    })
    const hideCrisp = () => {
      $crisp.push(['do', 'chat:hide'])
    }
    const setCrisp = () => {
      // TODO: this should be done in chat view, also inject javascript there
      window.CRISP_TOKEN_ID = authStore.account?.id
      $crisp.push(['do', 'session:reset'])
      $crisp.push(['set', 'user:nickname', authStore.account?.name])
      $crisp.push(['set', 'user:email', authStore.account?.email])
      $crisp.push(['set', 'user:phone', authStore.account?.phone])
      $crisp.push(['on', 'chat:closed', hideCrisp])
      hideCrisp()
    }
    const $vfm:any = inject('$vfm')
    onMounted(async () => {
      // uiStore.setMapTeleport('#mapTeleport')
      // TODO: this should be done in the the vs-address component pass token and use there
      if(!window.googleApi) {
        let places = document.createElement('script')
        places.setAttribute('type', 'text/javascript')
        places.setAttribute('src', 'https://maps.googleapis.com/maps/api/js?key=AIzaSyDDtmcTqrihzaWY6BPyaZDKKu7jQm51Gbg&libraries=places&callback=googleApiLoaded')
        places.async = true
        document.getElementsByTagName('head')[0].appendChild(places)
      }
      if (typeof navigator.splashscreen !== 'undefined') {
        navigator.splashscreen.hide()
      }
    })
    const notificationListener: Ref<any> = ref(null)
    watch(() => state.value.jwt, async (val, old) => {
      const currentUser = await firebaseTools.getCurrentUser()
      if (currentUser) {
        await authStore.setUser(currentUser)
        const customClaims = await firebaseTools.getCustomClaims(currentUser)
        if (typeof customClaims === 'undefined' || customClaims.version !== '2') return // wait for custom claims
        await authStore.setUser(currentUser)
        const hadNoAccount = authStore.account === null
        try {
          await authStore.ensureAccountLoaded({ user: currentUser, customClaims })
        } catch (error) {
          console.error(error)
          // TODO: retry on error after x seconds
          return
        }
        if (hadNoAccount && authStore.account) {
          if (URLInstanceID.value) await authStore.setInstanceID(URLInstanceID.value as string)
          setCrisp()
          await updateLastSeen()
          if (fcmToken.value) {
            setToken(fcmToken.value, 'fcm')
          }
          if (!fcmToken.value && window.os) {
            await authStore.getNotificationsToken()
          }
          const refURL = route.query.refURL
          if (route.path === '/login') {
            router.push(`${refURL ? refURL : '/'}`)
          }
        }
      }
    }, {deep: true})
    const setViewHeight = () => {
      let vh = window.innerHeight
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }
    onBeforeMount(async () => {
      setViewHeight()
      window.addEventListener('resize', () => {
        setViewHeight()
      })
      const user = await firebaseTools.getCurrentUser()
      if (user && !!state.value.jwt && !!state.value.customClaimsUpdate) {
        // console.info('ther is user', user)
        await authStore.setUser(user)
        const customClaims = await firebaseTools.getCustomClaims(user)
        if (typeof customClaims === 'undefined' || customClaims.version !== '2') return // wait for custom claims
        await authStore.setUser(user)
        const hadNoAccount = authStore.account === null
        try {
          await authStore.ensureAccountLoaded({ user, customClaims })
        } catch (error) {
          console.error(error)
        }
        if (hadNoAccount && authStore.account) {
          if (URLInstanceID.value) await authStore.setInstanceID(URLInstanceID.value as string)
          setCrisp()
          await updateLastSeen()
          if (fcmToken.value) {
            setToken(fcmToken.value, 'fcm')
          }
          if (!fcmToken.value && window.os) {
            await authStore.getNotificationsToken()
          }
        }
      }
    })
    const { fcmToken } = storeToRefs(authStore)
    watch(fcmToken, ()=>{
      if(fcmToken.value && !!state.value.jwt) {
        setToken(fcmToken.value, 'fcm')
      }
    })
    const router = useRouter()
    const route = useRoute()
    // const ul: any = (window as any).universalLinks;
    // if (typeof ul !== 'undefined') {
    //   ul.subscribe(null, function (eventData:any) {
    //     if (eventData.url !== location.href) {
    //       console.info({
    //         path: eventData.path,
    //         query: eventData.params,
    //       })
    //       router.push({
    //         path: eventData.path,
    //         query: eventData.params,
    //       })
    //     }
    //     console.log('Did launch application from the link: ' + eventData.url);
    //   });
    // }

    const instanceID = computed(() => {
      return authStore.instanceID
    })
    const URLInstanceID = computed(() => {
      return route.params.instanceID
    })
    watch(URLInstanceID, async () => {
      if (!URLInstanceID.value || URLInstanceID.value === 'undefined') {
        return
      }
      if (authStore.account && URLInstanceID.value) {
        await authStore.setInstanceID(URLInstanceID.value)  
      }
    }, {immediate: true})
    const unreads = ref()
    watch(instanceID, async (after, before) => {
      if (!before) {
        router.push({ name: route.name, params: { ...route.params }, query: route.query })
        return
      }
      router.push({ path: route.path.replace(before, after) })
    })
    const userInstancesWithPayments = computed(() => {
      return authStore.userInstances?.filter((instance: any) => {
          return instance.attributes.enabledPayments
        }) || []
    })
    const userInstancesWithoutHiddenDirectory = computed(() => {
      return authStore.userInstances?.filter((instance: any) => {
          return !instance.attributes.hideDirectory
        }) || []
    })
    const userPages = computed(()=> {
      return [
        {
          name: 'Inicio',
          icon: 'bxs:home-alt-2',
          path: '/user/map',
          id: 'user'
        },
        {
          name: 'Notificaciones',
          icon: 'ion:notifications-sharp',
          path: '/user/inbox/',
          id: 'inbox'
        },
        // {
        //   name: 'Reservas',
        //   icon: 'material-symbols-light:today',
        //   path: '/user/orders/',
        //   id: 'orders'
        // },
        ...(userInstancesWithoutHiddenDirectory.value.length > 0 ? [{
            name: 'Directorio',
            icon: 'material-symbols:headset-mic-outline',
            path: '/user/directory/',
            id: 'directory',
          },] : []),
        ...(userInstancesWithPayments.value.length > 0 ? [{
          name: 'Pagos',
          icon: 'ph:credit-card-bold',
          path: '/user/payments/',
          id: 'payments'
        }] : []),
        {
          name: 'Cuenta',
          icon: 'ph:user-circle-bold',
          path: '/account/',
          id: 'account'
        },
      ]
    })
    const adminPages = computed(() => {
      if (authStore.account !== null && authStore.instanceID) {
        return [
          // ...(authStore.isSupport ? [{
          //   name: 'Vision artificial',
          //   icon: 'ph:eye-bold',
          //   path: `/ai/`,
          //   id: 'ai',
          // }] : []),
          {
            name: 'Tablero',
            icon: 'ph:presentation-chart-bold',
            path: `/admin/${instanceID.value}/dashboard/`,
            id: 'dashboard',
            pinned: true,
          },
          {
            name: 'Datos',
            icon: 'ph:database-bold',
            id: 'data',
            group: true,
            pages: [
              {
                name: 'Rutas fijas',
                id: 'routes',
                icon: 'ph:path-bold',
                path: `/admin/${instanceID.value}/data/routes`,
              },
              {
                name: 'Pasajeros',
                id: 'parcels',
                icon: 'ph:person',
                path: `/admin/${instanceID.value}/data/parcels`,
              },
              {
                name: 'Vehículos',
                id: 'vehicles',
                icon: 'ph:bus',
                path: `/admin/${instanceID.value}/data/vehicles`,
              },
              {
                name: 'Personal',
                id: 'staff',
                icon: 'ph:users-three-bold',
                path: `/admin/${instanceID.value}/data/staff`,
              },
              {
                name: 'Lugares',
                id: 'stops',
                icon: 'ph:map-pin',
                path: `/admin/${instanceID.value}/data/stops`,
              },
              {
                name: 'Clientes',
                id: 'clients',
                icon: 'streamline:information-desk-customer',
                path: `/admin/${instanceID.value}/data/clients`,
              },
            ]
          },
          {
            name: 'Recaudos',
            id: 'charges',
            icon: 'solar:money-bag-outline',
            path: `/admin/${instanceID.value}/charges`,
          },
          // {
          //   name: 'Paradas',
          //   id: 'stops',
          //   icon: 'mdi:map-marker',
          //   path: `/admin/${instanceID.value}/stopViewer/`,
          // },
          {
            name: 'Novedades',
            id: 'temporals',
            icon: 'ph:arrows-clockwise-bold',
            path: `/admin/${instanceID.value}/temporals/`,
          },
          ...(!authStore.isReadOnly ? [{
            name: 'Planeador',
            icon: 'tdesign:map-route-planning',
            id: 'data',
            group: true,
            pages: [
              {
                name: 'Rutas fijas',
                id: 'plans',
                icon: 'material-symbols:calendar-month',
                path: `/admin/${instanceID.value}/plans/1`,
              },
              {
                name: 'Servicios diarios',
                id: 'plans',
                icon: 'material-symbols-light:today',
                path: `/admin/${instanceID.value}/trips/${new Date().toISOString().split('T')[0]}`,
              },
            ]
          }] : []),          // {
          //   name: 'Planeador',
          //   id: 'plans',
          //   icon: 'material-symbols:calendar-month',
          //   path: `/admin/${instanceID.value}/plans/1`,
          // },
          {
            name: 'Mapa de operación',
            id: 'viewer',
            icon: 'material-symbols:map-outline-rounded',
            path: `/admin/${instanceID.value}/viewer/`,
          },
          // ...(!authStore.isReadOnly ? [{
          //   name: 'Optimización',
          //   icon: 'ph:rocket-bold',
          //   path: `/admin/${instanceID.value}/optimization/`,
          //   id: 'optimization'
          // }] : []),
          ...(!authStore.isReadOnly ? [{
            name: 'Optimizador',
            icon: 'ph:rocket-bold',
            id: 'data',
            group: true,
            pages: [
              {
                name: 'Rutas fijas',
                id: 'fixedPlans',
                icon: 'material-symbols:calendar-month',
                path: `/admin/${instanceID.value}/classicOptimization/`,
              },
              {
                name: 'Servicios diarios',
                id: 'plans',
                icon: 'tdesign:map-route-planning',
                path: `/admin/${instanceID.value}/optimization/`,
              },
            ]
          }] : []),
          ...(!authStore.isReadOnly ? [{
            name: 'Directorio',
            icon: 'material-symbols:headset-mic-outline',
            path: `/admin/${instanceID.value}/directory/`,
            id: 'directory',
            alert: !!unreads.value
          }] : []),
          {
            name: 'Comunicados',
            icon: 'ph:megaphone-bold',
            path: `/admin/${instanceID.value}/notifications/`,
            id: 'notifications',
            pinned: true
          },
          {
            name: 'Histórico',
            icon: 'ph:clock-bold',
            path: `/admin/${instanceID.value}/history/`,
            id: 'history',
            pinned: true,
          },
          {
            name: 'Torre de control',
            icon: 'ph:globe-hemisphere-west-bold',
            path: `/admin/${instanceID.value}/tower/`,
            id: 'tower',
            pinned: true,
          },
          {
            name: 'Configuración',
            icon: 'ph:gear-bold',
            id: 'config',
            group: true,
            pages: [
            ...(!authStore.isReadOnly ? [{
                name: 'Contrato',
                icon: 'ph:file-cloud',
                path: `/admin/${instanceID.value}/contract/`,
                id: 'contract'
              }] : []),
              {
                name: 'Roles',
                icon: 'ph:users-bold',
                path: `/admin/${instanceID.value}/roles/`,
                id: 'roles'
              },
              // {
              //   name: 'Integraciones',
              //   icon: 'ph:plugs-bold',
              //   path: `/admin/${instanceID.value}/integrations/`,
              //   id: 'integrations'
              // },
              {
                name: 'Cuenta',
                icon: 'ph:user-circle-bold',
                path: '/account/',
                id: 'account'
              },
            ]
          },
        ]
      } else {
        return []
      }
    }) as any
    const activePage = ref('')
    watch(route, () => {
      activePage.value = (route.path as string) || ''
      uiStore.setMapAttrs({})
    })
    const handlePageSelect = (page: Record<string, string>) => {
      if (page.group) {
        if (page.pages) {
          router.push(page.pages[0]?.path)
        }
      }
      if (!page.path) return
      router.push(page.path)
    }
    const setToken = async (token, type) => {
      await registerToken(token, type)
    }
    const updateLastSeen = async () => {
      if (authStore.account) {
        const lastSeenMutation = [{
          action: 'update',
          table: 'users',
          pkey: { id: authStore.account.id },
          data: {
            lastSeen: new Date(),
          },
          returnKeys: 'id'
        }]
        // Every role, no-roles included, may update its own lastSeen
        await urqlMutation(lastSeenMutation, null, null, true)
      }
    }
    const isSearching = ref(false)
    const searchHasErrors = ref(false)
    const lastSearch = ref('')
    const showInstancePicker = ref(false)
    const searchResults = ref()
    const searchAbortController = ref<AbortController | null>(null)
    const runSearch = async (e) => {
      lastSearch.value = e

      // Cancel previous search request if it exists
      if (searchAbortController.value) {
        searchAbortController.value.abort()
      }

      if (!e) {
        searchResults.value = null
        searchAbortController.value = null
        return
      }

      // Create new AbortController for this search
      searchAbortController.value = new AbortController()
      const searchRequests = {
        searches: [
          {
            collection: 'parcels',
            query_by: 'name,documentID'
          },
          {
            collection: 'routes',
            query_by: 'name'
          },
          {
            collection: 'vehicles',
            query_by: 'name'
          },
          {
            collection: 'staff',
            query_by: 'name,phone'
          },
          {
            collection: 'stops',
            query_by: 'name,place,address'
          },
          {
            collection: 'clients',
            query_by: 'name,documentID'
          },
        ]
      }
      const commonSearchParams =  {
        q: e,
        prioritize_exact_match: true,
      }
      const searchOptions = {
        abortSignal: searchAbortController.value.signal
      }
      let searchResult = null
      const currentController = searchAbortController.value
      try {
        if (searchHasErrors.value) return
        if (!hasSearchKey()) throw new Error('Forbidden - a valid `x-typesense-api-key` header must be sent')
        searchResult = await client().multiSearch.perform(searchRequests, commonSearchParams, searchOptions)
      } catch (error: any) {
        // Check if this request was aborted - if so, don't process it
        if (error?.name === 'AbortError' || currentController?.signal.aborted) {
          return
        }
        if (((error as Error).message).includes('Forbidden')) {
          // Check again if aborted during the error handling
          if (currentController?.signal.aborted) return
          searchHasErrors.value = true
          await renovateClient(authStore.instanceID)
          searchHasErrors.value = false
          // Check one more time before retrying
          if (currentController?.signal.aborted) return
          searchResult = await client().multiSearch.perform(searchRequests, {...commonSearchParams, q: lastSearch.value})
        }
      }
      // Only process results if this search wasn't aborted
      if (currentController?.signal.aborted) return
      const tables = [
        {
          name:'Pasajeros',
          id: 'parcels',
        },
        {
          name:'Rutas',
          id: 'routes',
        },
        {
          name:'Vehículos',
          id: 'vehicles',
        },
        {
          name:'Personal',
          id: 'staff',
        },
        {
          name:'Lugares',
          id: 'stops',
        },
        {
          name:'Clientes',
          id: 'clients',
        },
      ]
      const routerResults = search(router.options.routes, ['name'], e)
      searchResults.value = [
        ...(await Promise.all(searchResult.results.map(async (result, index) => {
          let parcelAvatars = null
          if (tables[index].name === 'Pasajeros') {
            const parcelIDs = result.hits?.map(hit => `"${hit.document.id}"`)
            const query = gql`
              query getParcelsForOmniSearch {
                parcels(where: {id: {_in: [${parcelIDs}]}}) {
                  id
                  avatar
                }
              }
            `
            const {data, error} = await clientHandle.client.query(query, {}).toPromise()
            if (error) console.error(error)
            parcelAvatars = data.parcels
          }
          return {
            name: tables[index].name,
            avatar: !index,
            items: result?.hits?.map(hit => {
              const document = hit.document
              return {
                avatar: parcelAvatars?.find(parcel => parcel.id === document.id)?.avatar,
                name: document.name,
                footer: document.documentID || document.phone || document.address,
                mark: hit.highlights[0]?.snippet.split('>')[1].split('<')[0],
                id: document.id,
                path: (tables[index].name !== 'Lugares') ? `/admin/${instanceID.value}/data/${tables[index].id}/${document.id}` : `/admin/${instanceID.value}/addressEditor/null/${document.id}`,
              }
            })
          }
        }))),
        {
          name: 'Navegación',
          avatar: false,
          items: routerResults.map(result => {
            return {
              name: result.name,
              mark: e,
              path: result.path,
              icon: 'akar-icons:link-chain',
            }
          })
        }
      ]
    }
    const { emit: mapClickEmit } = useEventBus<string>('map:click')
    const handleMapClick = (e) => {
      mapClickEmit(e)
    }
    const { emit: markerDragEmit } = useEventBus<string>('marker:drag')
    const handleMarkerDrag = (e) => {
      markerDragEmit(e)
    }
    const { emit: infoClickEmit } = useEventBus<string>('marker:infoClick')
    const handleInfoClick = (e) => {
      infoClickEmit(e)
    }
    const { emit: markerClickEmit } = useEventBus<string>('marker:click')
    const handleMarkerClick = (e) => {
      markerClickEmit(e)
    }
    const getRouteKey = (path:string) => {
      if (path.includes('dashboard')) {
        return 'dashboard';
      }else{
        return path;
      }
    }
    const mapRef = ref(null)
    const setMapRef = (el) => {
      mapRef.value = el
    }
    expose({
      getMap: () => mapRef.value.getMap()
    })
    // The boot screen's phone line: index.html reads the last one from localStorage before any JS runs, so it
    // stays put from the first paint to the first view instead of appearing only while the account loads.
    const BOOT_CAPTION_KEY = 'rs:bootCaption'
    const readBootCaption = () => {
      try { return localStorage.getItem(BOOT_CAPTION_KEY) || '' } catch { return '' }
    }
    const bootCaption = ref(readBootCaption())
    watch(() => authStore.user?.phoneNumber || authStore.user?.email || '', (value, old) => {
      if (!value && !old) return
      bootCaption.value = value
      try {
        if (value) localStorage.setItem(BOOT_CAPTION_KEY, value)
        else localStorage.removeItem(BOOT_CAPTION_KEY) // signed out
      } catch { /* private mode: the line just starts empty next launch */ }
    }, { immediate: true })
    const shouldHaveInstanceID = computed(() => {
      if (route.path.includes('enrollment')) return true
      if (route.path.includes('suspended')) return true
      if (!!authStore.isAuthenticated && !!authStore.account && !!authStore.isAdmin) {
        return !!authStore.instanceID
      }
      return true
    })
    return {
      bootCaption,
      activeBusWalkthrough,
      shownChecks,
      closeBusWalkthrough,
      shouldHaveInstanceID,
      setMapRef,
      mapRef,
      uiStore,
      route,
      currentInstance,
      userPages,
      adminPages,
      activePage,
      handlePageSelect,
      authStore,
      isSearching,
      showInstancePicker,
      searchResults,
      runSearch,
      handleMapClick,
      handleMarkerDrag,
      handleInfoClick,
      handleMarkerClick,
      os,
      getRouteKey,
      showUpdateBanner,
      webviewUpdate,
      dismissUpdateBanner,
      reloadForUpdate,
      backendStalled,
      stallNotice
    }
  }
})
</script>

<style scoped>
.slide-left-enter-active, .slide-left-leave-active {
  transition: all 0.3s ease;
}

.slide-left-enter-from, .slide-left-leave-active {
  transform: translateX(-2em);
  opacity: 0;
}

.slide-right-enter-active, .slide-right-leave-active {
  transition: all 0.3s ease;
}

.slide-right-enter-from, .slide-right-leave-active {
  transform: translateX(2em);
  opacity: 0;
}
</style>